import { Affixes } from '../types/keystoneAffixTypes';
import { Attributes } from 'sequelize';
import { IKeystoneRunInstance } from '../backend/models/dungeonTypes';
import _ from 'lodash';

/**
 * Keystone utility functions shared between frontend and backend
 */

/**
 * Calculate the number of keystone upgrades (+0 to +3) based on clear time
 *
 * Timer thresholds:
 * - +3 (Gold): Clear time <= 60% of par time (adjusted)
 * - +2 (Silver): Clear time <= 80% of par time (adjusted)
 * - +1 (Bronze): Clear time <= par time
 * - +0 (Depleted): Clear time > par time
 *
 * @param dungeonParTimeMs - The dungeon's par time in milliseconds
 * @param clearTimeMs - The actual clear time in milliseconds
 * @param hasChallengersPeril - Whether the Challengers Peril affix was active (adds 90s to timer)
 * @returns Number of upgrades (0-3)
 */
export function calculateNumKeystoneUpgrades(
  dungeonParTimeMs: number,
  clearTimeMs: number,
  hasChallengersPeril?: boolean
): number {
  let silverRequiredMs = (dungeonParTimeMs - 999) * 0.8 + 999;
  let goldRequiredMs = (dungeonParTimeMs - 999) * 0.6 + 999;

  if (hasChallengersPeril) {
    // With challengers peril, we take the "original" gold/silver time, and add the perils 90s
    const originalTimer = dungeonParTimeMs - 90_000;
    silverRequiredMs = (originalTimer - 999) * 0.8 + 90_999;
    goldRequiredMs = (originalTimer - 999) * 0.6 + 90_999;
  }

  if (clearTimeMs <= goldRequiredMs) {
    return 3;
  }

  if (clearTimeMs <= silverRequiredMs) {
    return 2;
  }

  if (clearTimeMs <= dungeonParTimeMs) {
    return 1;
  }

  return 0;
}

export function getBaseAffix(affixes: (number | null)[]): Affixes | null {
  for (const affix of affixes) {
    if (affix === Affixes.FORTIFIED) {
      return Affixes.FORTIFIED;
    }

    if (affix === Affixes.TYRANNICAL) {
      return Affixes.TYRANNICAL;
    }
  }

  return null;
}

export function getRunBaseAffix(run: IKeystoneRunInstance): Affixes | null {
  const affixes = _.compact([run.affix_0_id, run.affix_1_id, run.affix_2_id, run.affix_3_id]);

  return getBaseAffix(affixes);
}

export function getAllAffixes(run: Attributes<IKeystoneRunInstance>): number[] {
  return _.compact([run.affix_0_id, run.affix_1_id, run.affix_2_id, run.affix_3_id]);
}
